import { motion } from "framer-motion";
import PhoneMockup from "@/components/landing/PhoneMockup";

const items = [
    {
        img: "/assets/screenshots/route-budget.png",
        alt: "Toplam yolculuk bütçesi ekranı — yakıt, HGS ve toplam tutar",
        eyebrow: "Rota maliyeti",
        title: "Toplam bütçe tek karede.",
        desc: "2.865,64 ₺ · 527,8 km · 5 sa 28 dk — yakıt ve HGS kalem kalem ayrışmış. Benzin, motorin ve LPG karşılaştırmalı tahmin.",
        tags: ["Canlı fiyat", "Tüm araç tipleri", "Dakikalık"],
        side: "left",
    },
    {
        img: "/assets/screenshots/radars-alts.png",
        alt: "Radar uyarıları ve alternatif rota seçenekleri ekranı",
        eyebrow: "Radar & alternatifler",
        title: "8 sabit radar, 2 alternatif rota.",
        desc: "OpenStreetMap kaynaklı sabit radar noktaları güzergâhına işlenir. En kısa veya en ucuz — hangi rotayı tercih edeceksin?",
        tags: ["OSM verisi", "En kısa / En ucuz", "Mola listesi"],
        side: "right",
    },
    {
        img: "/assets/screenshots/navigation.png",
        alt: "Canlı navigasyon ekranı — adım adım yönlendirme ve radar pinleri",
        eyebrow: "Canlı navigasyon",
        title: "Adım adım, sesli yönlendirme.",
        desc: "\"Hedefe gidiyorsun · 87,5 km · solda kalın.\" Harita üzerinde radar pinleri, Mapbox altlığı, hız ve limit takibi.",
        tags: ["Sesli uyarı", "Mapbox", "25 adımlık plan"],
        side: "left",
    },
    {
        img: "/assets/screenshots/services-3d.png",
        alt: "3D harita görünümü ve yakındaki hizmetler ekranı",
        eyebrow: "3D harita + hizmetler",
        title: "Yakındakileri tek dokunuşla aç.",
        desc: "İstasyon, restoran, mescit ve diğer kategoriler tek bottom sheet'te. Şehir içinde 3D bina görünümü ile nerede olduğunu anla.",
        tags: ["3D bina", "Kategori filtresi", "Hızlı keşfet"],
        side: "right",
    },
    {
        img: "/assets/screenshots/fuel-prices.png",
        alt: "EPDK kaynaklı akaryakıt fiyatları listesi ve şehir/marka filtresi",
        eyebrow: "EPDK akaryakıt",
        title: "81 il, tüm markalar, canlı fiyat.",
        desc: "Şehir, ilçe ve marka ile filtrele. Benzin, motorin ve LPG fiyatları EPDK kaynaklı veriyle eşlenir — 500+ sonuç anında.",
        tags: ["EPDK kaynağı", "Şehir / İlçe", "Marka filtresi"],
        side: "left",
    },
];

const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
    },
};

export default function Showcase() {
    return (
        <section
            id="vitrin"
            data-testid="showcase-section"
            className="relative py-28 sm:py-36 px-5 sm:px-8 max-w-7xl mx-auto"
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.7 }}
                className="max-w-2xl mb-20"
            >
                <div className="text-[#FF2D92] text-xs uppercase tracking-[0.22em] font-medium mb-5">
                    Uygulama vitrini
                </div>
                <h2 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                    Güzel görünür. <span className="grad-text">Hızlı çalışır.</span>
                </h2>
                <p className="mt-5 text-white/60 text-base sm:text-lg max-w-xl leading-relaxed">
                    Yol arkadaşlığının dijital hali. Her ekran tek bir iş için tasarlandı.
                </p>
            </motion.div>

            <div className="space-y-28 sm:space-y-40">
                {items.map((it, i) => {
                    const isLeft = it.side === "left";
                    return (
                        <div
                            key={i}
                            data-testid={`showcase-item-${i}`}
                            className={`grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center ${
                                isLeft ? "" : "md:[&>div:first-child]:order-2"
                            }`}
                        >
                            <motion.div
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, amount: 0.3 }}
                                className="relative"
                            >
                                <div className="relative max-w-[280px] mx-auto">
                                    <PhoneMockup data-testid={`phone-mockup-${i}`} bare>
                                        <img
                                            src={it.img}
                                            alt={it.alt}
                                            loading="lazy"
                                            className="absolute inset-0 w-full h-full object-cover"
                                        />
                                    </PhoneMockup>
                                </div>
                            </motion.div>

                            <motion.div
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ delay: 0.1 }}
                                className={isLeft ? "md:pl-4" : "md:pr-4"}
                            >
                                <div className="text-[#00F0FF] text-xs uppercase tracking-[0.22em] font-medium mb-4">
                                    {it.eyebrow}
                                </div>
                                <h3 className="font-display font-semibold text-3xl sm:text-4xl leading-[1.05] tracking-tight">
                                    {it.title}
                                </h3>
                                <p className="mt-5 text-white/60 text-base leading-relaxed max-w-md">
                                    {it.desc}
                                </p>

                                <div className="mt-8 flex flex-wrap gap-2">
                                    {it.tags.map((tag, ti) => (
                                        <span
                                            key={ti}
                                            className="px-3 py-1.5 rounded-full text-xs text-white/70 bg-white/5 border border-white/10"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
